import { useEffect, useRef, useCallback } from "react";

type LazyComponent = () => Promise<{ default: React.ComponentType<unknown> }>;

export const useLazyPreload = (components: LazyComponent[], rootMargin = "200px") => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasPreloaded = useRef(false);

  const preloadComponents = useCallback(() => {
    if (hasPreloaded.current) return;
    hasPreloaded.current = true;
    
    // Preload all lazy components
    components.forEach((component) => {
      component();
    });
  }, [components]);

  useEffect(() => {
    // Create a sentinel element at the fold
    const sentinel = document.createElement("div");
    sentinel.id = "preload-sentinel";
    sentinel.style.position = "absolute";
    sentinel.style.top = "100vh";
    sentinel.style.height = "1px";
    sentinel.style.width = "1px";
    sentinel.style.pointerEvents = "none";
    document.body.appendChild(sentinel);

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            preloadComponents();
            observerRef.current?.disconnect();
          }
        });
      },
      { rootMargin }
    );

    observerRef.current.observe(sentinel);

    return () => {
      observerRef.current?.disconnect();
      sentinel.remove();
    };
  }, [preloadComponents, rootMargin]);

  return { preloadComponents };
};
